import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import { useRef, useState } from 'react'
import type { ChecklistBreadcrumbSegment } from '../../utils/checklistBreadcrumb'
import type { ChecklistParentSaveOutcome } from '../../types/checklistDetailController'
import { StatusChip } from '../Common'
import { Pressable } from '../Common/Pressable'
import { ParentPickerPopover, type ParentPickerContext } from './ParentPickerPopover'

type Props = {
  segments: ChecklistBreadcrumbSegment[]
  className?: string
  /** Hide the leading BOOK segment. The action bar renders the book chip
   *  next to Status/Edit/Delete instead so it never appears twice. */
  hideBookSegment?: boolean
  /** When true, non-BOOK crumbs render as buttons that open the parent
   *  picker. Requires `onReparent` + `parentPickerContext`. */
  editable?: boolean
  onReparent?: (
    nextParentId: string | null,
    opts?: { window?: { start: string; end: string } | null },
  ) => Promise<ChecklistParentSaveOutcome>
  parentPickerContext?: ParentPickerContext
}

/**
 * Compact breadcrumb strip rendered at the top of the checklist details
 * popup. Segments are supplied by whichever surface opened the popup so the
 * displayed path matches the user's current location. When `editable` is on,
 * crumbs become buttons that open a rule-aware parent picker.
 */
export const ChecklistBreadcrumb = ({
  segments,
  className,
  hideBookSegment = false,
  editable = false,
  onReparent,
  parentPickerContext,
}: Props) => {
  const visible = hideBookSegment ? segments.filter((segment) => segment.kind !== 'BOOK') : segments
  const [pickerOpen, setPickerOpen] = useState(false)
  const chipRef = useRef<HTMLButtonElement | null>(null)

  if (visible.length === 0 && !editable) return null

  const canEditLocation = editable && Boolean(onReparent) && Boolean(parentPickerContext)

  return (
    <nav
      className={['chkBreadcrumb', className ?? ''].filter(Boolean).join(' ')}
      aria-label="Checklist location"
    >
      {visible.map((segment, index) => {
        const isLast = index === visible.length - 1
        const isEditableCrumb = canEditLocation && segment.kind !== 'BOOK' && isLast
        return (
          <span
            key={`${segment.kind}-${index}-${segment.targetId ?? segment.label}`}
            className={`chkBreadcrumb__seg chkBreadcrumb__seg--${segment.kind.toLowerCase()}`}
          >
            {isEditableCrumb ? (
              <Pressable
                ref={chipRef}
                className="chkBreadcrumb__label chkBreadcrumb__label--editable"
                aria-haspopup="dialog"
                aria-expanded={pickerOpen}
                onClick={() => setPickerOpen((prev) => !prev)}
              >
                <span>{segment.label}</span>
                <EditRoundedIcon fontSize="inherit" aria-hidden />
              </Pressable>
            ) : (
              <span className="chkBreadcrumb__label">{segment.label}</span>
            )}
            {!isLast && (
              <ChevronRightRoundedIcon
                className="chkBreadcrumb__sep"
                fontSize="inherit"
                aria-hidden
              />
            )}
          </span>
        )
      })}
      {canEditLocation && visible.length === 0 && (
        // Nothing to hang the picker off — render a lone "Set parent" chip.
        <Pressable
          ref={chipRef}
          className="chkBreadcrumb__label chkBreadcrumb__label--editable chkBreadcrumb__label--empty"
          aria-haspopup="dialog"
          aria-expanded={pickerOpen}
          onClick={() => setPickerOpen((prev) => !prev)}
        >
          Set parent
        </Pressable>
      )}
      {canEditLocation && pickerOpen && onReparent && parentPickerContext && (
        <ParentPickerPopover
          mode="reparent"
          anchorRef={chipRef}
          onClose={() => setPickerOpen(false)}
          onReparent={onReparent}
          context={parentPickerContext}
        />
      )}
    </nav>
  )
}

/** Compact chip for a single BOOK segment — rendered inside the action bar
 *  so the book type stays visible without eating the top strip. */
export const ChecklistBookChip = ({ segment, className }: {
  segment: ChecklistBreadcrumbSegment
  className?: string
}) => {
  if (segment.kind !== 'BOOK') return null
  return (
    <span
      className={['chkBookChip', className ?? ''].filter(Boolean).join(' ')}
      title={segment.label}
    >
      <StatusChip size="sm" tone="neutral">
        {segment.label}
      </StatusChip>
    </span>
  )
}

/** Split segments into the book segment (if present) and everything else. */
export const splitBookSegment = (
  segments: ChecklistBreadcrumbSegment[],
): { book: ChecklistBreadcrumbSegment | null; rest: ChecklistBreadcrumbSegment[] } => {
  const book = segments.find((segment) => segment.kind === 'BOOK') ?? null
  const rest = book ? segments.filter((segment) => segment !== book) : segments
  return { book, rest }
}
